import React, { useContext, useState, useEffect, useRef } from 'react';
import { FiMenu, FiUser, FiBell, FiAlertCircle, FiInfo, FiShoppingCart, FiAlertTriangle } from 'react-icons/fi';
import { useLocation, Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import DashboardContext from '../context/DashboardContext';
import api from '../services/api';

const pageTitles = {
  '/': 'Tableau de bord',
  '/stock': 'Gestion du Stock',
  '/sales': 'Ventes',
  '/debts': 'Dettes Clients',
  '/clients': 'Clients',
  '/suppliers': 'Fournisseurs',
  '/expenses': 'Dépenses',
  '/supplier-debts': 'Dettes Fournisseurs',
  '/reports': 'Rapports',
  '/profile': 'Profil',
};

const PlanBadge = ({ plan }) => {
  const badgeStyles = {
    essentiel: 'bg-gray-200 text-gray-700',
    pro: 'bg-blue-100 text-brand-blue',
    premium: 'bg-purple-200 text-purple-800',
  };

  if (!plan) return null;

  return (
    <span className={`hidden md:inline-block text-xs font-bold px-2 py-0.5 rounded-full ${badgeStyles[plan] || 'bg-gray-200'}`}>
      {plan.charAt(0).toUpperCase() + plan.slice(1)}
    </span>
  );
};

const Navbar = ({ setSidebarOpen }) => {
  const { user } = useContext(AuthContext);
  const { dashboardData } = useContext(DashboardContext) || {};
  const location = useLocation();
  const title = pageTitles[location.pathname.replace('/dashboard', '')] || 'Tableau de bord';
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);

  // Fermer les notifications si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      if (!navigator.onLine) return; // STOP : Ne pas chercher si hors ligne
      const newNotifs = [];

      try {
        // 1. Commandes en ligne (Fetch direct pour avoir les dernières commandes)
        const salesRes = await api.get('/sales');
        let allSales = [];
        if (Array.isArray(salesRes.data)) {
            allSales = salesRes.data;
        } else if (salesRes.data && Array.isArray(salesRes.data.sales)) {
            allSales = salesRes.data.sales;
        } else if (salesRes.data && Array.isArray(salesRes.data.data)) {
            allSales = salesRes.data.data;
        }

        if (allSales && allSales.length > 0) {
             const orders = allSales.filter(s => {
                const status = s.status ? s.status.toLowerCase() : '';
                return ['pending', 'en attente', 'commande en ligne', 'order_placed', 'created', 'pending_confirmation'].includes(status);
            });
            
            if (orders.length > 0) {
              newNotifs.push({ 
                id: 'orders', 
                type: 'info', 
                icon: <FiShoppingCart className="text-blue-600" />,
                message: `${orders.length} commande(s) en ligne à valider`, 
                link: '/dashboard',
                highlight: true
              });
            }
        }
      } catch (e) {
        console.error("Erreur notifs sales", e);
      }

      // 2. Stock & Dettes (Via DashboardContext pour éviter double appel)
      if (dashboardData) {
        const lowStockCount = dashboardData.lowStockProducts?.filter(p => !p.isService).length || 0;
        if (lowStockCount > 0) {
          newNotifs.push({ 
            id: 'stock', 
            type: 'warning', 
            icon: <FiAlertTriangle className="text-orange-500" />,
            message: `${lowStockCount} produits en rupture de stock`, 
            link: '/dashboard/stock?filter=low_stock' 
          });
        }
  
        if (dashboardData.overdueDebts > 0) {
          newNotifs.push({ 
            id: 'debts', 
            type: 'error', 
            icon: <FiAlertCircle className="text-red-500" />,
            message: `${dashboardData.overdueDebts} dettes clients en retard`, 
            link: '/dashboard/debts' 
          });
        }
      }
      
      setNotifications(newNotifs);
    };
    fetchNotifications();

    // Polling toutes les 15 secondes pour une réactivité "instantanée"
    const interval = setInterval(fetchNotifications, 15000);
    
    // Rafraîchir immédiatement au focus de la fenêtre (quand l'utilisateur revient sur l'onglet)
    const handleFocus = () => fetchNotifications();
    window.addEventListener('focus', handleFocus);

    return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
    };
  }, [user, location.pathname, dashboardData]);

  return (
    <header className="flex justify-between items-center p-4 bg-white border-b h-16">
      <button onClick={() => setSidebarOpen(true)} className="text-gray-500 focus:outline-none lg:hidden">
        <FiMenu className="h-6 w-6" />
      </button>
      <h1 className="text-xl font-bold text-gray-800 font-poppins hidden md:block">{title}</h1>
      
      <div className="flex items-center gap-4">
        {/* Centre de Notifications */}
        <div className="relative" ref={notifRef}>
          <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors">
            <FiBell className="h-6 w-6" />
            {notifications.length > 0 && (
              <span className="absolute top-1 right-1 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
            )}
          </button>
          
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-fade-in">
              <div className="p-3 border-b border-gray-50 bg-gray-50 flex justify-between items-center">
                <h4 className="text-sm font-bold text-gray-700">Notifications</h4>
                <span className="text-xs text-gray-500">{notifications.length} alertes</span>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map(notif => (
                    <Link 
                      key={notif.id} 
                      to={notif.link} 
                      onClick={() => setShowNotifications(false)}
                      className={`flex items-start p-4 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${notif.highlight ? 'bg-blue-50/40' : ''}`}
                    >
                      <div className="mt-1 mr-3 flex-shrink-0">
                        {notif.icon || (notif.type === 'error' ? <FiAlertCircle className="text-red-500" /> : <FiInfo className="text-blue-500" />)}
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${notif.type === 'error' ? 'text-red-600' : 'text-gray-800'}`}>{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">Cliquez pour voir les détails</p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <FiBell className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Aucune nouvelle notification</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Link to="/dashboard/profile" className="flex items-center space-x-3 cursor-pointer bg-gray-100 hover:bg-gray-200 py-1.5 px-3 rounded-full transition-colors">
          <FiUser className="h-5 w-5 text-gray-600" />
          <span className="hidden md:block font-medium text-gray-700">{user?.username || 'Utilisateur'}</span>
          <PlanBadge plan={user?.plan} />
        </Link>
      </div>
    </header>
  );
};

export default Navbar;